import { useMemo } from 'react';
import { useCustomSearchParams } from '@hooks/UseCustomSearchParams2';
import useQueryObject from '@hooks/useQueryObjectSql';
import { keepPreviousData } from '@tanstack/react-query';
import { and, field, or } from '@utils/filter';
import { filterToExpression, isEmptyFilter, isEmptyObject } from '@utils/filterUtils';
import { deserializeSortItem } from '@utils/utils';
import { DwQueryRequest, SortDir } from '@views/CustomDashboards/types';
import { PREVIEW_PAGE_SIZE } from '../types';
import { generateNestedSqlAccessor } from '../utils';

export const useMappingPreviewData = ({
  page,
  workflowId,
  enabled,
  instanceId,
}: {
  page: number;
  workflowId: string | undefined;
  enabled?: boolean;
  instanceId?: string;
}) => {
  const [filters] = useCustomSearchParams({});
  const orderBy = deserializeSortItem(filters?.orderBy?.[0] ? `rawlogs.DOCUMENT.${filters?.orderBy?.[0]}` : 'rawlogs.AVALOR_ID__DESC');

  const queryEnabled = enabled && !!workflowId;

  const sourceFilter = useMemo(
    () =>
      instanceId
        ? field('rawlogs.dsi_id').str.equals(instanceId)
        : or(field('rawlogs.dsi_id').str.equals(''), field('rawlogs.dsi_id').str.empty()),
    [instanceId]
  );

  const workflowFilter = useMemo(() => field("AVALOR_CONTEXT(rawlogs, 'workflowId')").str.equals(workflowId as string), [workflowId]);

  const whereFilter = useMemo(
    () =>
      filterToExpression({}, Object.fromEntries(Object.entries(filters).map(([key, value]) => [generateNestedSqlAccessor(key), value]))),
    [filters]
  );

  const allFilters = useMemo(
    () => [sourceFilter, workflowFilter, ...(!isEmptyObject(whereFilter) && !isEmptyFilter(whereFilter) ? [whereFilter] : [])],
    [sourceFilter, workflowFilter, whereFilter]
  );

  const dataQuery: DwQueryRequest = {
    select: {
      dims: [{ name: 'rawlogs.DOCUMENT' }, { name: orderBy.property }],
      metrics: [],
    },
    filter: and(...allFilters),
    sorting: [
      {
        name: orderBy.property,
        dir: orderBy.isAsc ? SortDir.ASC : SortDir.DESC,
      },
    ],
    top: {
      size: PREVIEW_PAGE_SIZE,
      offset: (page || 0) * PREVIEW_PAGE_SIZE,
      groupOthers: false,
    },
    projectionId: {
      name: 'rawlogs',
      builtIn: true,
    },
    distinct: false,
  };

  const { data, totalRowCount, isLoading, isRefetching } = useQueryObject({
    queryObject: dataQuery,
    options: { enabled: queryEnabled, placeholderData: keepPreviousData },
    onSuccess: data => data.map(d => d['rawlogs.DOCUMENT']),
  });

  return useMemo(
    () => ({
      data,
      totalRowCount,
      isLoading,
      isRefetching,
    }),
    [data, totalRowCount, isLoading, isRefetching]
  );
};
